import { Group, Stack } from "@mantine/core"
import { PaginationState } from "@tanstack/react-table"
import { ColumnManager, Loader } from "@tfg/ui"
import { Dispatch, SetStateAction, useState } from "react"

import { useCustomReactTable } from "~/components/hooks/useCustomReactTable"
import { CustomTable } from "~/components/table/CustomTable"
import {
  ExportConfigurationDto,
  ExportRequestResponseDto,
  useGetExportConfigurations,
} from "~/gen/api"
import { useGetColumnManagerTranslations } from "~/hooks/table"
import { ExportConfigurationsSelect } from "~/pages/Brand/DataTransfer/Export/ExportConfigurationsSelect"
import { GenerateExportActions } from "~/pages/Brand/DataTransfer/Export/GenerateExportAction"
import { useColumns } from "~/pages/Brand/DataTransfer/Export/columns"

type Props = {
  data: ExportRequestResponseDto[]
  total: number
  pagination: PaginationState
  setPagination: Dispatch<SetStateAction<PaginationState>>
  isFetching: boolean
}

export const ExportRequestsTable: React.FC<Props> = ({
  data,
  total,
  pagination,
  setPagination,
  isFetching,
}: Props) => {
  const { data: exportConfigurationsQuery, isPending: isLoading } =
    useGetExportConfigurations()
  const columnManagerTranslations = useGetColumnManagerTranslations()
  const columns = useColumns()
  const [selectedExportConfiguration, setSelectedExportConfiguration] =
    useState<ExportConfigurationDto | undefined>(undefined)
  const [isColumnManagerOpen, setIsColumnManagerOpen] = useState(false)
  const table = useCustomReactTable({
    data,
    columns,
    state: {
      pagination,
    },
    pageCount: Math.ceil(total / pagination.pageSize),
    manualPagination: true,
    onPaginationChange: setPagination,
  })

  if (isLoading) return <Loader fullPage />

  return (
    <Stack>
      <Group justify="-moz-initial">
        <ExportConfigurationsSelect
          selectedExportConfiguration={selectedExportConfiguration}
          setSelectedExportConfiguration={setSelectedExportConfiguration}
          exportConfigurationsData={exportConfigurationsQuery?.data}
        />
        <GenerateExportActions
          selectedExportConfiguration={selectedExportConfiguration}
        />
        <ColumnManager
          table={table}
          translations={columnManagerTranslations}
          isOpen={isColumnManagerOpen}
          setIsOpen={setIsColumnManagerOpen}
        />
      </Group>
      <CustomTable
        table={table}
        setOpenColumnManager={setIsColumnManagerOpen}
        manualPaginationCount={total}
        isFetching={isFetching}
      />
    </Stack>
  )
}
